import { Link } from "react-router-dom";
import { RouteEnum } from "../../enums/RouteEnum";
import { useAppDispatch, useAppLocation } from "../../../app/hooks";
import {
    menuToggle,
    useGetUserInfoQuery,
    useMarkRefurbishedVisitedMutation,
} from "../../../app/rootReducer";
import { JSX, useEffect, useMemo, useState } from "react";
import { useRefurbishedShopEnabled } from "../../hooks/useRefurbishedShopEnabled";
import { ReactComponent as CycleSvg } from "../../../common/svgs/cycle.svg";
import { ReactComponent as ProductRangeSvg } from "../../../common/svgs/product-range.svg";
import { ReactComponent as CollectionsSvg } from "../../../common/svgs/collections.svg";
import { ReactComponent as GridViewSvg } from "../../../common/svgs/grid-view.svg";
import { ReactComponent as AccountSvg } from "../../../common/svgs/account.svg";
import { ReactComponent as Awesome } from "../../../common/svgs/awesome.svg";
import { shouldShowRefurbishedNewTag } from "../../utils/shouldShowRefurbishedNewTag";
import { useStringTranslation } from "../../hooks/useStringTranslation";

export const Navigation = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const location = useAppLocation();

    const userData = useGetUserInfoQuery();
    const [markRefurbishedVisited] = useMarkRefurbishedVisitedMutation();
    const { refurbishedShopEnabled, isUserLoading } =
        useRefurbishedShopEnabled();
    const { t, translations } = useStringTranslation();

    const [showRefurbishedNewTag, setShowRefurbishedNewTag] = useState(false);

    useEffect(() => {
        if (!userData?.data) return;

        setShowRefurbishedNewTag(shouldShowRefurbishedNewTag(userData?.data));
    }, [userData?.data, userData?.isSuccess]);

    const menuItems = useMemo(() => {
        const items = [
            {
                logo: ProductRangeSvg,
                label: t("product-range"),
                route: RouteEnum.PRODUCT_RANGE,
            },
            {
                logo: CycleSvg,
                label: t("refurbished"),
                route: RouteEnum.REFURBISHED,
                newTag: showRefurbishedNewTag,
            },
            {
                logo: CollectionsSvg,
                label: t("collections"),
                route: RouteEnum.COLLECTIONS,
            },
            {
                logo: GridViewSvg,
                label: t("pricebook"),
                route: RouteEnum.PRICEBOOK,
            },
            {
                logo: AccountSvg,
                label: t("account"),
                route: RouteEnum.ACCOUNT,
            },
        ];

        const showRefurbishedMenuItem = isUserLoading || refurbishedShopEnabled;

        if (!showRefurbishedMenuItem) {
            return items.filter((item) => item.route !== RouteEnum.REFURBISHED);
        }

        return items;
    }, [t, showRefurbishedNewTag, refurbishedShopEnabled, isUserLoading]);

    return (
        <nav className="l-app__menu__nav">
            <ul className="u-reset-ul">
                {menuItems.map((menuItem) => {
                    let className = "";

                    if (location.pathname.includes(menuItem.route)) {
                        className += "isActive";
                    }

                    const MenuIcon = menuItem.logo;

                    return (
                        <li
                            className={className}
                            key={"dashboard-navigation: " + menuItem.route}
                        >
                            <Link
                                to={menuItem.route}
                                onClick={() => {
                                    dispatch(menuToggle());

                                    if (
                                        menuItem.route ===
                                            RouteEnum.REFURBISHED &&
                                        showRefurbishedNewTag
                                    ) {
                                        setShowRefurbishedNewTag(false);
                                        void markRefurbishedVisited();
                                    }
                                }}
                            >
                                <div className="icon">
                                    <MenuIcon />
                                </div>
                                {!translations ? (
                                    <>
                                        <span className="skeleton skeleton-navigation"></span>
                                    </>
                                ) : (
                                    <div className={"l-app__menu__nav__item"}>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: menuItem.label,
                                            }}
                                        />
                                        {menuItem.newTag && (
                                            <div
                                                className={
                                                    "l-app__menu__nav__tag"
                                                }
                                            >
                                                <Awesome
                                                    style={{ color: "#FFCF00" }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
